import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Layout, 
  Type, 
  MousePointer2, 
  CheckCircle2, 
  Loader2,
  Sparkles,
  Smartphone,
  Monitor,
  Globe,
  Search,
  ExternalLink,
  Settings,
  BarChart2,
  Shield,
  Zap,
  TrendingUp,
  HelpCircle,
  Wand2,
  Lock
} from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useToast } from './Toast';
import { executeFlowAction } from '../services/gemini';
import { PageContext } from '../types';
import { markChecklistItemCompleted } from '../src/lib/checklist';

interface PageEditorProps {
  pageId: string;
  onBack: () => void;
  userData: any;
}

export const PageEditor: React.FC<PageEditorProps> = ({ pageId, onBack, userData }) => {
  const { showToast } = useToast();
  const [pageData, setPageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [activeTab, setActiveTab] = useState<'content' | 'seo' | 'domain' | 'metrics'>('content');

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const docRef = doc(db, 'landing_pages', pageId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPageData(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching page:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [pageId]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await updateDoc(doc(db, 'landing_pages', pageId), pageData);
      setSaveSuccess(true);
      showToast('Página salva com sucesso!', 'success');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving page:", error);
      showToast('Erro ao salvar página.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOptimize = async () => {
    if (!pageData) return;

    // Proactive check for API Key
    const currentKey = localStorage.getItem('OPTIVELO_GEMINI_KEY') || (process.env.GEMINI_API_KEY as string) || (process.env.API_KEY as string);
    const isStudioEnv = typeof window !== 'undefined' && (window as any).aistudio;

    if (!isStudioEnv && (!currentKey || currentKey === 'undefined' || currentKey === '')) {
      showToast('Chave de API não configurada. Por favor, configure-a no menu "Redator Publicitário IA".', 'error');
      return;
    }

    setIsOptimizing(true);
    try {
      const context: PageContext = {
        productName: pageData.name || '',
        description: pageData.subheadline || '',
        currentHeadline: pageData.headline || '',
        currentCTA: pageData.cta || '',
        traffic: pageData.traffic || 0,
        conversionsA: pageData.conversions || 0,
        conversionsB: 0,
        ctrA: 0,
        ctrB: 0,
        bounceRate: 0,
        avgTimeOnPage: 0,
        daysActive: 0,
        totalLeads: pageData.conversions || 0,
        trafficSource: 'Direto',
        deviceType: 'Computador',
        plan: 'pro'
      };

      const result = await executeFlowAction('otimizar_copy', context);
      
      // Sanitização robusta da resposta JSON da IA
      let cleaned = result.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\n?/, '').replace(/\n?```$/, '');
      else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\n?/, '').replace(/\n?```$/, '');
      
      const parsed = JSON.parse(cleaned);
      
      if (parsed.code === 'MISSING_API_KEY' || (parsed.error && (parsed.error.includes('Chave de API') || parsed.error.includes('API_KEY')))) {
        showToast('Configuração de IA Pendente. Ative seu motor Quantum nas configurações.', 'info');
        return;
      }

      if (parsed.error) {
        showToast(parsed.error, 'error');
        return;
      }

      if (parsed.headline) updateField('headline', parsed.headline);
      if (parsed.subheadline) updateField('subheadline', parsed.subheadline);
      if (parsed.cta) updateField('cta', parsed.cta);
      
      showToast('Copy otimizada pela IA!', 'success');
    } catch (error) {
      console.error("Error optimizing copy:", error);
      showToast('Erro ao otimizar copy.', 'error');
    } finally {
      setIsOptimizing(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setPageData((prev: any) => ({ ...prev, [field]: value }));
    if (field === 'traffic' || field === 'conversions') {
      markChecklistItemCompleted('traffic');
    }
  };

  const updateBenefit = (index: number, value: string) => {
    const newBenefits = [...(pageData.benefits || [])];
    newBenefits[index] = value;
    updateField('benefits', newBenefits);
  };

  if (loading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="p-8 text-center text-slate-500">
        Página não encontrada.
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0b0e14] animate-fade-in overflow-hidden">
      {/* Editor Header */}
      <header className="h-16 border-b border-white/5 px-4 lg:px-6 flex items-center justify-between bg-black/50 backdrop-blur-xl shrink-0">
        <div className="flex items-center gap-2 lg:gap-4">
          <button 
            onClick={onBack}
            className="p-2 text-slate-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="h-6 w-px bg-white/10 hidden sm:block"></div>
          <div className="hidden sm:block">
            <h2 className="text-sm font-bold text-white truncate max-w-[150px]">{pageData.name}</h2>
            <p className="text-[12px] text-slate-500 uppercase tracking-widest font-bold">Editor Visual</p>
          </div>
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          {saveSuccess && (
            <div className="hidden md:flex items-center gap-2 text-emerald-500 animate-fade-in">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-[12px] font-bold uppercase tracking-widest">Salvo!</span>
            </div>
          )}
          <div className="flex bg-white/5 rounded-lg p-1">
            <button 
              onClick={() => setPreviewMode('desktop')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'desktop' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setPreviewMode('mobile')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'mobile' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className={`px-3 lg:px-4 py-2 text-white text-[12px] lg:text-xs font-bold rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 ${
              saveSuccess ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-indigo-500 shadow-indigo-500/20 hover:bg-indigo-400'
            }`}
          >
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : saveSuccess ? <CheckCircle2 className="w-3 h-3" /> : <Save className="w-3 h-3" />}
            <span className="hidden sm:inline">{saveSuccess ? 'Salvo!' : 'Salvar'}</span>
          </button>
          <a 
            href={pageData.url || `/p/${pageId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 lg:px-4 lg:py-2 bg-white/5 border border-white/10 text-white text-xs font-bold rounded-xl hover:bg-white/10 transition-all flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4 lg:w-3 lg:h-3" />
            <span className="hidden lg:inline">Ver ao Vivo</span>
          </a>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Sidebar: Controls */}
        <aside className="w-full lg:w-80 border-r border-white/5 bg-black/20 flex flex-col h-[40vh] lg:h-full shrink-0">
          {/* Sidebar Tabs */}
          <div className="flex border-b border-white/5 p-2 gap-1">
            <button 
              onClick={() => setActiveTab('content')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-bold uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${activeTab === 'content' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}
            >
              <Layout className="w-4 h-4" />
              Conteúdo
            </button>
            <button 
              onClick={() => setActiveTab('seo')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-bold uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${activeTab === 'seo' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}
            >
              <Search className="w-4 h-4" />
              SEO
            </button>
            <button 
              onClick={() => setActiveTab('domain')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-bold uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${activeTab === 'domain' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}
            >
              <Globe className="w-4 h-4" />
              Domínio
            </button>
            <button 
              onClick={() => setActiveTab('metrics')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-bold uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${activeTab === 'metrics' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}
            >
              <BarChart2 className="w-4 h-4" />
              Métricas
            </button>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-6 pb-24 space-y-8">
            {activeTab === 'content' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[12px] font-bold text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                      <Settings className="w-3 h-3" /> Geral
                    </h3>
                    <button 
                      onClick={handleOptimize}
                      disabled={isOptimizing}
                      className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[12px] font-bold uppercase tracking-widest rounded-lg hover:bg-indigo-500/20 transition-all disabled:opacity-50"
                    >
                      {isOptimizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Otimizar IA
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Nome da Página (Interno)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          Nome para sua organização interna. Não aparece para o visitante.
                        </div>
                      </div>
                    </div>
                    <input 
                      type="text"
                      value={pageData.name || ''}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="premium-input text-sm"
                      placeholder="Ex: Lançamento Black Friday"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                   <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                     <Type className="w-3 h-3" /> Conteúdo Principal
                   </h3>
                   
                   <div className="space-y-2">
                     <div className="flex items-center gap-2">
                       <label className="micro-label">Título Principal (Headline)</label>
                       <div className="group relative">
                         <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                         <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                           O título principal da sua página. Deve ser curto e impactante.
                         </div>
                       </div>
                     </div>
                    <textarea 
                      value={pageData.headline || ''}
                      onChange={(e) => updateField('headline', e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none resize-none min-h-[100px]"
                      placeholder="O melhor título para seu produto..."
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Subtítulo (Subheadline)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          Explica com mais detalhes a promessa do título.
                        </div>
                      </div>
                    </div>
                    <textarea 
                      value={pageData.subheadline || ''}
                      onChange={(e) => updateField('subheadline', e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none resize-none min-h-[80px]"
                      placeholder="Explique o valor do seu produto..."
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                    <MousePointer2 className="w-3 h-3" /> Chamada para Ação
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Texto do Botão (CTA)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          O que o usuário deve fazer? Ex: "Comprar Agora", "Quero Testar".
                        </div>
                      </div>
                    </div>
                    <input 
                      type="text"
                      value={pageData.cta || ''}
                      onChange={(e) => updateField('cta', e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none"
                      placeholder="Ex: Começar Agora"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Link do Botão (URL)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          Para onde o usuário será levado ao clicar no botão.
                        </div>
                      </div>
                    </div>
                    <input 
                      type="text"
                      value={pageData.ctaUrl || ''}
                      onChange={(e) => updateField('ctaUrl', e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none"
                      placeholder="Ex: https://checkout.exemplo.com"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3" /> Benefícios
                  </h3>
                  {[0, 1, 2].map((idx) => (
                    <div key={idx} className="space-y-2">
                      <label className="micro-label">Benefício {idx + 1}</label>
                      <input 
                        type="text"
                        value={pageData.benefits?.[idx] || ''}
                        onChange={(e) => updateBenefit(idx, e.target.value)}
                        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none"
                        placeholder={`Benefício ${idx + 1}...`}
                      />
                    </div>
                  ))}
                </div>

                <div className="pt-6 border-t border-white/5">
                  <button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saveSuccess ? 'Página Salva!' : 'Salvar Alterações'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="space-y-4">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                    <Search className="w-3 h-3" /> Otimização SEO
                  </h3>
                  <p className="text-[12px] text-slate-600 leading-relaxed">Configure como sua página aparece no Google e redes sociais.</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Título SEO (Meta Title)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          O título que aparece na aba do navegador e no Google.
                        </div>
                      </div>
                    </div>
                    <input 
                      type="text"
                      value={pageData.seoTitle || ''}
                      onChange={(e) => updateField('seoTitle', e.target.value)}
                      className="premium-input text-sm"
                      placeholder="Ex: OptiVelo | Otimização de Vendas"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">Descrição SEO (Meta Description)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          O texto que aparece abaixo do título nos resultados de busca.
                        </div>
                      </div>
                    </div>
                    <textarea 
                      value={pageData.seoDescription || ''}
                      onChange={(e) => updateField('seoDescription', e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none resize-none min-h-[120px]"
                      placeholder="Uma breve descrição para os motores de busca..."
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="micro-label">Palavras-chave (Separadas por vírgula)</label>
                    <input 
                      type="text"
                      value={pageData.seoKeywords || ''}
                      onChange={(e) => updateField('seoKeywords', e.target.value)}
                      className="premium-input text-sm"
                      placeholder="Ex: marketing, vendas, ia, conversão"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5">
                  <button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saveSuccess ? 'SEO Salvo!' : 'Salvar Alterações SEO'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'domain' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="space-y-4">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                    <Globe className="w-3 h-3" /> Endereço da Página
                  </h3>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="micro-label">URL da Página (Slug)</label>
                      <div className="group relative">
                        <HelpCircle className="w-3 h-3 text-slate-700 cursor-help hover:text-indigo-400 transition-colors" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[12px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                          O final do endereço da sua página. Ex: optivelo.com.br/p/minha-oferta
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-slate-600 font-mono">/p/</span>
                      <input 
                        type="text"
                        value={pageData.slug || pageId}
                        onChange={(e) => updateField('slug', e.target.value)}
                        className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none"
                        placeholder="slug-da-pagina"
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[12px] font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                        <Layout className="w-3 h-3" /> Estrutura de Site
                      </h4>
                      {userData?.role !== 'admin' && !['scale', 'enterprise'].includes(userData?.subscriptionStatus) && (
                        <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-[8px] font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-1">
                          <Lock className="w-2 h-2" /> Plano Escale
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] text-slate-600 leading-relaxed">Transforme sua landing page em um site completo.</p>
                    
                    <div className={`space-y-4 ${userData?.role !== 'admin' && !['scale', 'enterprise'].includes(userData?.subscriptionStatus) ? 'opacity-50 pointer-events-none' : ''}`}>
                      <div className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-xl">
                        <div className="space-y-0.5">
                          <p className="text-[12px] font-bold text-white uppercase tracking-widest">Menu de Navegação</p>
                          <p className="text-[11px] text-slate-500">Adiciona um menu no topo da página.</p>
                        </div>
                        <button 
                          onClick={() => updateField('hasNavbar', !pageData.hasNavbar)}
                          className={`w-10 h-5 rounded-full transition-colors relative ${pageData.hasNavbar ? 'bg-indigo-500' : 'bg-slate-800'}`}
                        >
                          <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${pageData.hasNavbar ? 'left-6' : 'left-1'}`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-xl">
                        <div className="space-y-0.5">
                          <p className="text-[12px] font-bold text-white uppercase tracking-widest">Rodapé Completo</p>
                          <p className="text-[11px] text-slate-500">Adiciona um rodapé com links e redes sociais.</p>
                        </div>
                        <button 
                          onClick={() => updateField('hasFooter', !pageData.hasFooter)}
                          className={`w-10 h-5 rounded-full transition-colors relative ${pageData.hasFooter ? 'bg-indigo-500' : 'bg-slate-800'}`}
                        >
                          <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${pageData.hasFooter ? 'left-6' : 'left-1'}`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/5 space-y-4">
                    <h4 className="text-[12px] font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                      <Shield className="w-3 h-3" /> Domínio Customizado
                    </h4>
                    <p className="text-[12px] text-slate-600 leading-relaxed">Conecte seu próprio domínio para uma experiência profissional.</p>
                    
                    <div className="space-y-2">
                      <label className="micro-label">Seu Domínio</label>
                      <input 
                        type="text"
                        value={pageData.customDomain || ''}
                        onChange={(e) => updateField('customDomain', e.target.value)}
                        className="premium-input text-sm"
                        placeholder="Ex: oferta.seusite.com"
                      />
                    </div>

                    <div className="p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-xl space-y-2">
                       <p className="text-[11px] font-bold text-indigo-300 uppercase tracking-widest">Configuração DNS</p>
                       <p className="text-[11px] text-slate-500">Aponte um registro CNAME para: <code className="text-white">cname.optivelo.com.br</code></p>
                     </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5">
                  <button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saveSuccess ? 'Domínio Salvo!' : 'Salvar Alterações de Domínio'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'metrics' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="space-y-4">
                  <h3 className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                    <BarChart2 className="w-3 h-3" /> Performance em Tempo Real
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="premium-card p-4 space-y-1">
                      <p className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Tráfego Total</p>
                      <p className="text-2xl font-bold text-white tracking-tighter">{pageData.traffic || 0}</p>
                    </div>
                    <div className="premium-card p-4 space-y-1">
                      <p className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Conversões</p>
                      <p className="text-2xl font-bold text-emerald-500 tracking-tighter">{pageData.conversions || 0}</p>
                    </div>
                  </div>

                  <div className="premium-card p-4 space-y-1">
                    <p className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Taxa de Conversão</p>
                    <div className="flex items-end gap-2">
                      <p className="text-3xl font-bold text-indigo-400 tracking-tighter">
                        {pageData.traffic > 0 ? ((pageData.conversions / pageData.traffic) * 100).toFixed(1) : '0.0'}%
                      </p>
                      <TrendingUp className="w-4 h-4 text-indigo-500 mb-1.5" />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/5 space-y-4">
                    <h4 className="text-[12px] font-bold text-amber-400 uppercase tracking-widest flex items-center gap-2">
                      <Zap className="w-3 h-3" /> Simulação de Tráfego
                    </h4>
                    <p className="text-[12px] text-slate-600 leading-relaxed">Ajuste os números para testar como o Dashboard se comporta.</p>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="micro-label">Simular Tráfego</label>
                        <input 
                          type="number"
                          value={pageData.traffic || 0}
                          onChange={(e) => updateField('traffic', Number(e.target.value))}
                          className="premium-input text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="micro-label">Simular Conversões</label>
                        <input 
                          type="number"
                          value={pageData.conversions || 0}
                          onChange={(e) => updateField('conversions', Number(e.target.value))}
                          className="premium-input text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5">
                  <button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500 text-white text-[12px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saveSuccess ? 'Métricas Salvas!' : 'Salvar Alterações de Métricas'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* Main Content: Live Preview */}
        <main className="flex-1 bg-[#12151a] p-4 lg:p-12 overflow-y-auto custom-scrollbar flex flex-col items-center">
          <div className={`transition-all duration-500 shadow-2xl rounded-[40px] border border-white/5 overflow-hidden flex flex-col ${
            pageData.theme === 'dark' ? 'bg-[#0f172a]' : 'bg-white'
          } ${
            previewMode === 'mobile' ? 'w-[375px] h-[667px]' : 'w-full max-w-4xl min-h-[800px]'
          }`}>
            {/* Browser/Mobile Chrome */}
            <div className="h-12 border-b border-white/5 bg-white/[0.02] flex items-center px-6 gap-3 shrink-0">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500/20"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/20"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/20"></div>
              </div>
              <div className="flex-1 bg-white/5 rounded-full h-6 flex items-center px-4 gap-2 overflow-hidden">
                <Globe className="w-3 h-3 text-slate-600 shrink-0" />
                <span className="text-[12px] text-slate-500 truncate">
                  {pageData.customDomain || `optivelo.com.br/p/${pageData.slug || pageId}`}
                </span>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 flex flex-col items-center overflow-y-auto custom-scrollbar relative">
              {/* Site Navbar Preview */}
              {pageData.hasNavbar && (
                <div className="w-full h-16 border-b border-white/5 bg-black/50 backdrop-blur-md flex items-center justify-between px-8 sticky top-0 z-20">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-indigo-500 flex items-center justify-center">
                      <Zap className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-bold text-white tracking-tighter">{pageData.name}</span>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                    <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                    <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                  </div>
                </div>
              )}

              <div className="flex-1 p-6 lg:p-12 flex flex-col items-center justify-center text-center space-y-6 w-full max-w-3xl mx-auto">
                <div className="space-y-4 max-w-2xl mx-auto">
                <h1 className={`tracking-tight leading-normal break-words mx-auto ${pageData.theme === 'dark' ? 'text-white' : 'text-slate-900'} ${
                  previewMode === 'mobile' 
                    ? ((pageData.headline || '').length > 65 ? 'text-[13px] sm:text-sm font-bold' : 'text-base font-black') 
                    : ((pageData.headline || '').length > 65 ? 'text-lg sm:text-xl md:text-2xl font-bold max-w-2xl' : 'text-xl sm:text-2xl md:text-3xl font-black max-w-xl')
                }`}>
                  {pageData.headline || 'Seu Título Magnético Aqui'}
                </h1>
                {pageData.subheadline && (
                  <p className={`font-medium leading-relaxed ${pageData.theme === 'dark' ? 'text-slate-400' : 'text-slate-600'} ${previewMode === 'mobile' ? 'text-[10px]' : 'text-xs sm:text-sm'}`}>
                    {pageData.subheadline}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-center gap-6 w-full max-w-md mx-auto">
                {pageData.template === 'leads' ? (
                  <div className="w-full p-6 bg-white/[0.03] border border-white/10 rounded-[32px] space-y-4 text-left">
                    <div className="space-y-2">
                      <div className="h-2 w-20 bg-white/10 rounded-full"></div>
                      <div className="h-10 w-full bg-white/5 border border-white/10 rounded-xl"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-24 bg-white/10 rounded-full"></div>
                      <div className="h-10 w-full bg-white/5 border border-white/10 rounded-xl"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-32 bg-white/10 rounded-full flex items-center gap-2">
                        <span>WhatsApp (Opcional)</span>
                      </div>
                      <div className="h-10 w-full bg-white/5 border border-white/10 rounded-xl"></div>
                    </div>
                    <button className={`w-full bg-indigo-500 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/20 py-4 text-sm mt-4`}>
                      {pageData.cta || 'Começar Agora'}
                    </button>
                  </div>
                ) : (
                  <button className={`w-full bg-indigo-500 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-400 transition-all ${previewMode === 'mobile' ? 'py-4 text-sm' : 'py-5 text-lg'}`}>
                    {pageData.cta || 'Começar Agora'}
                  </button>
                )}
                
                <div className="grid gap-3 w-full">
                  {(pageData.benefits || []).map((benefit: string, i: number) => (
                    benefit && (
                      <div key={i} className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/5 rounded-2xl text-left">
                        <div className="w-6 h-6 bg-indigo-500/10 rounded-lg flex items-center justify-center text-indigo-400 shrink-0">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <span className="text-xs lg:text-sm text-slate-300 font-medium">{benefit}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>

              <div className="pt-12 opacity-20 flex items-center gap-2 pb-12">
                <Sparkles className="w-4 h-4" />
                <span className="text-[12px] font-bold uppercase tracking-widest">Desenvolvido por OptiVelo</span>
              </div>

              {/* Site Footer Preview */}
              {pageData.hasFooter && (
                <div className="w-full py-12 border-t border-white/5 bg-black/50 px-8 grid grid-cols-2 gap-8 text-left">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-indigo-500 flex items-center justify-center">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs font-bold text-white tracking-tighter">{pageData.name}</span>
                    </div>
                    <p className="text-[12px] text-slate-500 leading-relaxed">Construído com a inteligência neural do OptiVelo.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                      <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                      <div className="h-1 w-8 bg-white/10 rounded-full"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
